import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Download } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { AuditLogDetails } from "./AuditLogDetails";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

interface AuditLog {
  id: string;
  table_name: string;
  record_id: string;
  action: string;
  old_data: any;
  new_data: any;
  changed_fields: string[];
  changed_by: string | null;
  changed_by_name?: string;
  changed_by_email?: string;
  changed_at: string;
}

const IMPORTANT_TABLES = [
  { value: "all", label: "All Tables" },
  { value: "members", label: "Members" },
  { value: "admin_users", label: "Admin Users" },
  { value: "conferences", label: "Conferences" },
  { value: "contact_messages", label: "Messages" },
  { value: "activities", label: "Activities" },
];

export function AuditLogViewer() {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [availableTables, setAvailableTables] = useState<string[]>([]);
  const [tableFilter, setTableFilter] = useState<string>("all");
  const [actionFilter, setActionFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { toast } = useToast();
  const logsPerPage = 20;

  useEffect(() => {
    fetchAvailableTables();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [tableFilter, actionFilter, page]);

  const fetchAvailableTables = async () => {
    try {
      const { data, error } = await supabase
        .from("audit_logs")
        .select("table_name")
        .order("table_name");

      if (error) throw error;

      const uniqueTables = Array.from(
        new Set(data?.map((row) => row.table_name) || [])
      ).sort();
      setAvailableTables(uniqueTables);
    } catch (error: any) {
      console.error("Error fetching tables:", error);
    }
  };

  const fetchLogs = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from("audit_logs")
        .select("*", { count: "exact" })
        .order("changed_at", { ascending: false })
        .range((page - 1) * logsPerPage, page * logsPerPage - 1);

      if (tableFilter !== "all") {
        query = query.eq("table_name", tableFilter);
      }

      if (actionFilter !== "all") {
        query = query.eq("action", actionFilter);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      // Fetch admin user details for logs with changed_by
      const logsWithUsers = await Promise.all(
        (data || []).map(async (log) => {
          if (log.changed_by) {
            const { data: adminUser } = await supabase
              .from("admin_users")
              .select("full_name, email")
              .eq("id", log.changed_by)
              .single();

            return {
              ...log,
              changed_by_name: adminUser?.full_name,
              changed_by_email: adminUser?.email,
            };
          }
          return log;
        })
      );

      setLogs(logsWithUsers);
      setTotalPages(Math.ceil((count || 0) / logsPerPage));
    } catch (error: any) {
      toast({
        title: "Error loading audit logs",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getActionBadge = (action: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive"> = {
      INSERT: "default",
      UPDATE: "secondary",
      DELETE: "destructive",
    };
    return <Badge variant={variants[action] || "default"}>{action}</Badge>;
  };

  const formatChangedFields = (log: AuditLog) => {
    if (!log.changed_fields || log.changed_fields.length === 0) return "N/A";
    if (log.changed_fields.length > 3) {
      return `${log.changed_fields.slice(0, 3).join(", ")}... (+${log.changed_fields.length - 3})`;
    }
    return log.changed_fields.join(", ");
  };

  const filteredLogs = logs.filter((log) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      log.table_name.toLowerCase().includes(searchLower) ||
      log.action.toLowerCase().includes(searchLower) ||
      log.record_id.toLowerCase().includes(searchLower) ||
      log.changed_by_name?.toLowerCase().includes(searchLower) ||
      log.changed_by_email?.toLowerCase().includes(searchLower)
    );
  });

  const exportToCSV = () => {
    const headers = ["Table", "Action", "Record ID", "Changed Fields", "Changed By", "Changed At"];
    const rows = filteredLogs.map((log) => [
      log.table_name,
      log.action,
      log.record_id,
      log.changed_fields?.join("; ") || "",
      log.changed_by_name || log.changed_by_email || "System",
      format(new Date(log.changed_at), "yyyy-MM-dd HH:mm:ss"),
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <Alert>
        <AlertTitle>Enhanced Audit Logs</AlertTitle>
        <AlertDescription>
          View detailed change history with expandable rows showing before/after comparisons.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Audit Logs</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Track all database changes with detailed field-level comparisons
              </p>
            </div>
            <Button onClick={exportToCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="space-y-4">
            <Tabs value={tableFilter} onValueChange={setTableFilter}>
              <TabsList className="grid w-full grid-cols-6">
                {IMPORTANT_TABLES.map((table) => (
                  <TabsTrigger key={table.value} value={table.value}>
                    {table.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Search by table, action, record ID, or user..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="INSERT">Insert</SelectItem>
                  <SelectItem value="UPDATE">Update</SelectItem>
                  <SelectItem value="DELETE">Delete</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Table</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Record ID</TableHead>
                  <TableHead>Changed Fields</TableHead>
                  <TableHead>Changed At</TableHead>
                  <TableHead>Changed By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <LoadingSpinner />
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <>
                      <TableRow
                        key={log.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                      >
                        <TableCell>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            {expandedRow === log.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </Button>
                        </TableCell>
                        <TableCell className="font-medium capitalize">
                          {log.table_name}
                        </TableCell>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.record_id.substring(0, 8)}...
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatChangedFields(log)}
                        </TableCell>
                        <TableCell className="text-sm">
                          {format(new Date(log.changed_at), "MMM d, yyyy HH:mm")}
                        </TableCell>
                        <TableCell className="text-sm">
                          {log.changed_by_name ? (
                            <div>
                              <div className="font-medium">{log.changed_by_name}</div>
                              <div className="text-xs text-muted-foreground">
                                {log.changed_by_email}
                              </div>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">System</span>
                          )}
                        </TableCell>
                      </TableRow>
                      {expandedRow === log.id && (
                        <TableRow>
                          <TableCell colSpan={7} className="bg-muted/20">
                            <AuditLogDetails log={log} />
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
